import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ArrowRight, ShoppingBag } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UNIVERS_CONFIG = {
  anniversaire: {
    title: 'Anniversaires',
    desc: 'Créez un anniversaire mémorable avec nos animations et décorations',
    event_type: 'anniversaire_enfant',
    hero_bg: 'rgba(249,197,209,0.08)',
    badge: 'Très populaire',
  },
  mariage: {
    title: 'Mariages',
    desc: 'Des prestations premium pour votre jour J',
    event_type: 'mariage',
    hero_bg: 'rgba(212,175,55,0.08)',
    badge: 'Premium',
  },
  entreprise: {
    title: 'Entreprise & Professionnels',
    desc: 'Animations et cocktails pour vos événements d’entreprise',
    event_type: 'entreprise',
    hero_bg: 'rgba(197,216,249,0.08)',
    badge: 'Tarifs CE disponibles',
  },
  jeux: {
    title: 'Jeux & Animations',
    desc: 'Mini golf, blind test, jeux bois géants et bien plus',
    event_type: 'anniversaire_adulte',
    hero_bg: 'rgba(61,220,151,0.05)',
    badge: 'Nouveautés',
  },
  gourmand: {
    title: 'Animations gourmandes',
    desc: 'Barbe à papa, popcorn, crêpes, bar à bonbons…',
    event_type: 'anniversaire_enfant',
    hero_bg: 'rgba(255,204,102,0.06)',
    badge: 'Bestseller',
  },
  prestations: {
    title: 'Nos prestations',
    desc: 'Tous nos services et animations sur-mesure pour vos événements',
    event_type: null,
    hero_bg: 'rgba(147,133,236,0.08)',
    badge: 'Services & animations',
  },
};

export default function UniversPage({ univers: universProp }) {
  const { slug } = useParams();
  const isPrestationsRoute = !universProp && !slug;
  const universKey = isPrestationsRoute ? 'prestations' : (universProp || slug || 'anniversaire');
  const config = UNIVERS_CONFIG[universKey] || UNIVERS_CONFIG.anniversaire;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    const url = isPrestationsRoute
      ? `${BACKEND}/api/products?product_type=prestation&limit=48`
      : `${BACKEND}/api/configurateur/products/${config.event_type}?profile=particulier&limit=12`;
    fetch(url)
      .then(r => r.json())
      .then(d => setProducts(isPrestationsRoute ? (d.products || []) : (d || [])))
      .catch((err) => { /* eslint-disable-line no-empty */ })
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [config.event_type, isPrestationsRoute]);

  return (
    <div style={{ background: 'var(--bg, #FBF7F1)', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <div className="section-py" style={{ background: config.hero_bg, borderBottom: '1px solid #2A2A2A' }}>
        <div className="container-ose text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'rgba(212,175,55,0.12)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.25)' }}>
            {config.badge}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--text)', letterSpacing: '-0.02em' }}>{config.title}</h1>
          <p className="text-base max-w-lg mx-auto mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>{config.desc}</p>
          <Link to="/creer-evenement" className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
            Créer mon événement <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Products */}
      <div className="container-ose section-py">
        <h2 className="font-display text-2xl font-bold mb-6" style={{ color: 'var(--ink)' }}>Nos prestations</h2>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Aucune prestation publiée pour le moment.
            </p>
            <Link to="/creer-evenement" className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
              Créer mon événement <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map(p => (
              <div key={p.id} className="card-premium overflow-hidden">
                <Link to={`/produit/${p.slug || p.id}`}>
                  <img src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=70'} alt={p.nom}
                    className="w-full h-44 object-cover transition-transform duration-300 hover:scale-105" loading="lazy" />
                </Link>
                <div className="p-4">
                  <Link to={`/produit/${p.slug || p.id}`}><h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--ink)' }}>{p.nom}</h3></Link>
                  {isPrestationsRoute && p.duree && (
                    <p className="text-xs mb-1 font-medium" style={{ color: '#9385EC' }}>⏱ {p.duree}</p>
                  )}
                  <p className="text-xs mb-3 line-clamp-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{p.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="price-gold">
                      {(p.prix_profil || p.tarifs?.particulier) ? `à partir de ${p.prix_profil || p.tarifs?.particulier}€` : 'Sur devis'}
                    </span>
                    <button onClick={() => { addItem(p); toast.success('Ajouté'); }} className="btn-gold px-3 py-1.5 text-xs rounded-lg font-semibold">
                      <ShoppingBag size={12} className="inline mr-1" />Ajouter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
